import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, FileText, Compass, HeartHandshake, Lock, CheckCircle2, Flag, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Overline, ProgressBar, Loading, UpgradeBanner } from "@/components/shared";

export default function Dashboard() {
  const { entitlements } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <Overline>Welcome back</Overline>
        <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2">Hello, {data.name}.</h1>
        <p className="text-nm-neutral mt-3 text-lg font-light max-w-2xl">
          {entitlements.has_part_1 ? "Pick up where you left off, or start a new module." : "Explore the previews below, then unlock the parts you need."}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Continue */}
        <div className="lg:col-span-2 rounded-3xl border border-nm-primary/12 bg-nm-primary p-8 text-white nm-shadow-md relative overflow-hidden" data-testid="continue-card">
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-nm-coral/20 blur-2xl" />
          <div className="relative">
            <Overline className="text-nm-highlight">Continue where you left off</Overline>
            {data.continue ? (
              <>
                <div className="text-sm text-white/70 mt-4">{data.continue.section_label}</div>
                <h2 className="font-serif text-3xl mt-1">{data.continue.title}</h2>
                <p className="text-white/80 mt-3 max-w-lg leading-relaxed">{data.continue.summary}</p>
                <Link to={`/app/chapter/${data.continue.slug}`} data-testid="continue-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
                  <PlayCircle size={18} /> Resume chapter <ArrowRight size={16} />
                </Link>
              </>
            ) : (
              <>
                <h2 className="font-serif text-3xl mt-4">Start with the essentials</h2>
                <p className="text-white/80 mt-3 max-w-lg leading-relaxed">The Start Here module orients you fast, then points you to the highest-leverage chapters for a new manager.</p>
                <Link to="/app/start-here" data-testid="start-here-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
                  <Compass size={18} /> Go to Start Here <ArrowRight size={16} />
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="rounded-3xl border border-nm-primary/12 bg-nm-surface p-8 nm-shadow-sm" data-testid="progress-card">
          <Overline>Your Part 1 progress</Overline>
          <div className="flex items-end gap-2 mt-4">
            <span className="font-serif text-5xl text-nm-primary">{data.part1_progress.pct}%</span>
            <span className="text-sm text-nm-neutral mb-2">complete</span>
          </div>
          <div className="mt-4"><ProgressBar pct={data.part1_progress.pct} /></div>
          <div className="text-sm text-nm-neutral mt-3">{data.part1_progress.completed} of {data.part1_progress.total} chapters completed</div>
          <Link to="/app/part/part_1" data-testid="progress-view-chapters-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-nm-primary hover:gap-2.5 transition-[gap] duration-200">
            View all chapters <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      {/* Modules */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ModuleCard to="/app/part/part_1" locked={!entitlements.has_part_1} title="Part 1: The Role"
          desc="Understand the role, understand the work, run the work, and build your team." icon={Compass} part="part_1" />
        <ModuleCard to="/app/part/part_2" locked={!entitlements.has_part_2} title="Part 2: The People"
          desc="Trust, coaching, feedback, and leading people through change." icon={HeartHandshake} part="part_2" plum />
        <ModuleCard href="https://toolkit.managemeantexcellence.com" title="Part 3: The Moments"
          desc="Hard conversations and real-world scenarios — handled with a ready-to-use scenario guide." icon={Flag} gold />
      </div>

      {!entitlements.bundle_owner && <UpgradeBanner entitlements={entitlements} />}

      {/* Recommended */}
      {data.recommended?.length > 0 && (
        <div>
          <Overline>Recommended next</Overline>
          <h2 className="font-serif text-2xl text-nm-ink mt-2 mb-5">Where a new manager should start</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {data.recommended.map((c, i) => (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Link to={`/app/chapter/${c.slug}`} data-testid={`rec-${c.slug}`}
                  className="group block h-full rounded-2xl border border-nm-primary/10 bg-nm-surface p-6 nm-shadow-sm hover:-translate-y-1 transition-transform duration-300">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-nm-coral">{c.section_label}</div>
                  <div className="font-serif text-xl text-nm-ink mt-2">{c.title}</div>
                  <div className="flex items-center gap-1.5 text-sm text-nm-primary mt-4 group-hover:gap-2.5 transition-[gap] duration-200">Read <ArrowRight size={14} /></div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Featured templates */}
      <div>
        <Overline>Featured templates</Overline>
        <h2 className="font-serif text-2xl text-nm-ink mt-2 mb-5">Ready to use with your team today</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {data.featured_templates.map((t) => (
            <Link key={t.slug} to={`/app/template/${t.slug}`} data-testid={`featured-${t.slug}`}
              className="group block rounded-2xl border border-nm-green/20 bg-nm-green/[0.05] p-6 nm-shadow-sm hover:-translate-y-1 transition-transform duration-300">
              <FileText size={20} className="text-nm-green" />
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-nm-green mt-4">{t.category}</div>
              <div className="font-serif text-lg text-nm-ink mt-1.5">{t.title}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ to, href, locked, title, desc, icon: Icon, plum, gold }) {
  const accent = gold ? "amber" : plum ? "plum" : "primary";
  const border = gold ? "border-amber-300/50 bg-amber-50/60" : plum ? "border-nm-plum/20 bg-nm-plum/[0.04]" : "border-nm-primary/12 bg-nm-surface";
  const iconCls = gold ? "bg-amber-100 text-amber-600" : plum ? "bg-nm-plum/12 text-nm-plum" : "bg-nm-primary/10 text-nm-primary";
  const linkCls = gold ? "text-amber-700" : plum ? "text-nm-plum" : "text-nm-primary";
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${iconCls}`}><Icon size={22} /></div>
        {href ? <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700"><ExternalLink size={12} /> Scenario guide</span>
          : locked ? <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${iconCls}`}><Lock size={12} /> Locked</span>
          : <span className="inline-flex items-center gap-1.5 rounded-full bg-nm-green/12 px-3 py-1 text-xs font-medium text-nm-green"><CheckCircle2 size={12} /> Unlocked</span>}
      </div>
      <h3 className="font-serif text-2xl text-nm-ink mt-5">{title}</h3>
      <p className="text-nm-neutral mt-2 leading-relaxed">{desc}</p>
      <div className={`flex items-center gap-1.5 text-sm mt-5 group-hover:gap-2.5 transition-[gap] duration-200 ${linkCls}`}>
        {href ? <>Open the guide <ExternalLink size={14} /></> : <>{locked ? "Preview & unlock" : "Open module"} <ArrowRight size={15} /></>}
      </div>
    </>
  );
  const cls = `group relative block rounded-3xl border p-8 nm-shadow-sm hover:-translate-y-1 transition-transform duration-300 ${border}`;
  if (href)
    return <a href={href} target="_blank" rel="noopener noreferrer" data-testid="module-part_3" className={cls}>{inner}</a>;
  return <Link to={to} data-testid={`module-${to.split("/").pop()}`} className={cls}>{inner}</Link>;
}
